import { useEffect, useState } from 'react';
import Link from './Link.jsx';

const projects = [
  { 
    id: 'mmrt', 
    title: 'MMRT', 
    year: '2022-2026', 
    front_image: 'media/mmrt/circ2025.jpg', 
    front_subtitle: '2025 Canadian International Rover Challenge in Alberta, Canada.',
    gallery: [
      'media/mmrt/winter.jpg',
      'media/mmrt/swerve_v2.gif',
      'media/mmrt/arm_v2.jpg',
      'media/mmrt/curie_arm.jpg',
      'media/mmrt/circ2024.jpg',
      'media/mmrt/elevator.jpg',
      'media/mmrt/faraday1.jpg',
      'media/mmrt/faraday_touching_grass.png',
      'media/mmrt/maxwell.jpg',
      'media/mmrt/stuff.jpg',
      'media/mmrt/aura.jpg',
    ],
    description: 'A volume of technical projects detailing everything I have contributed to the McMaster Mars Rover Team (MMRT).',
    catchphrase: 'McMaster Mars Rover Team',
    details: (
      <>
        The <a href="https://www.mcmastermarsrover.com/" target="_blank" rel="noreferrer">McMaster Mars Rover Team (MMRT)</a> is a 
         student-driven technical club at McMaster University that competes in university-level rover competitions such as <a href="https://circ.cstag.ca/" target="_blank" rel="noreferrer">CIRC</a>, <a href="https://urc.marssociety.org/" target="_blank" rel="noreferrer">URC</a>, and <a href="https://roverchallenge.eu/" target="_blank" rel="noreferrer">ERC</a>. 
         The purpose of this team is to empower students with collaborative, hands-on engineering experience, and allowed me to express my continual interests
         in robots and share them with others. I officially joined the team in October 2022 and since May 2024 have taken up the role of software lead, 
         designing software to remotely control all of the rovers during competition tasks.<br></br>
         <br></br> What specific capabilities the rovers possess varies from year to year, but generally, <u>drivetrain and arm teleoperation,
         GPS and IMU localization, camera streaming, and autonomous navigation are the main areas of focus, all of which I have dabbled in</u>.
         This page serves as both a compilation of my past work on the team and a technical reference for any existing or future members who happen to stumble upon it.
         There is a lot of content to cover so to make it easier to digest, I've split it into three main sections each corresponding to the rovers I worked on.
      </>
    ),
    sections: [
      { 
        title: 'Faraday (2022 - 2023)', 
        body: (
            <>
              <div style={{display: 'flex', 'align-items': 'center'}}>
                  <img src={`${process.env.PUBLIC_URL}/media/mmrt/faraday2.jpg`} alt="Faraday with Arm (V1)"></img>
                  <img src={`${process.env.PUBLIC_URL}/media/mmrt/faraday_outside.png`} alt="Faraday at comp"></img>
              </div>
              For the team's first rover which we named Faraday, we used a <u>Jetson TX2 running ROS melodic</u> as our main framework. <u>ROS</u> in general
              is very useful especially for our application, as it allows us to easily integrate multiple sensors and actuators into one cohesive, node-based system. 
              Because we use it everywhere, it'll be referenced a lot throughout this page.<br></br>
              <br></br>
              <div style={{display: 'flex', 'align-items': 'center', gap: '20px'}}>
                  <p>
                  I was mainly responsible for writing <u>STM32</u> bluepill firmware on a custom DMC (Drive Motor Controller) board 
                  to <u>track the encoder odometry of all six drivetrain wheels and publish that data downstream to the rover's odom and nav stack via rosserial</u>.
                  That includes both velocity which was already computed and used for closed-loop <u>PID</u> control, and position which I had to integrate over time to get the displacement.
                  It was thanks to this that the rover was able to accurately localize itself and thus navigate somewhat autonomously in the traversal task in CIRC 2023.
                  Along the way, I also implemented a simple heartbeat timer in the event the rover loses connection while driving, allowing the motors to stop and
                  prevent it from causing any harm.
                  </p>
                  <figure className="dmc-figure">
                    <img src={`${process.env.PUBLIC_URL}/media/mmrt/dmc_blu.png`} alt="DMC Bluepill in Altium"></img>
                    <figcaption>DMC Board with STM32 Bluepill Shield, supports up to six motors and encoders.</figcaption>
                  </figure>
              </div>
              Around a month before comp in 2023, we discovered in that year's search and rescue task where the rover is required to locate and move a dummy astronaut
              to a safe zone, we had to connect to a Wi-Fi beacon attached to the astronaut's suit to get its approximate GPS position rather than being given them
              directly beforehand. So, to complete this task successfully, <u>I quickly wrote a simple script that attempts to connect to the beacon's network and scrape the coordinates off the html page it serves.</u> It
              did work, although not very well, likely because the beacon itself didn't broadcast its signal well enough to be picked up by the Jetson, so it was only effective 
              at close range, approx. 5 meters. Nonetheless, it did perform its job and along with other successful efforts got us enough points to <u>jump us high in the rankings that year 
              (4<sup>th</sup> place)</u>.<br></br>
              <br></br>
              That wraps up the glamorous work on Faraday that's worthy of mention. In the background, I also tuned <u>PID</u> values for the drivetrain motors which was a lot more
              difficult that I expected since the rover had to be suspended on a stand while I tuned each of them individually without the proper feedback we would 
              receive if it were on the ground. The video shows me tuning them early on, so excuse all the jittering. In the end, I learned a lot about PID control and
              how each term contributes to the response of a system.
              <video controls>
                <source src={`${process.env.PUBLIC_URL}/media/mmrt/faraday_demo.mp4`} type="video/mp4" />
              </video>
              <div className="sources-subsection">
                <h3>Extra Sources</h3>
                <ul>
                  <li><a href="https://github.com/MacRover/Faraday" target="_blank" rel="noreferrer">MacRover/Faraday</a></li>
                </ul>
              </div>
            </>
        )
      },
      { 
        title: 'Maxwell (2023 - 2025)', 
        body: (
          <>
            Maxwell, or V2, is MMRT's second iterative rover design that came into play right after CIRC 2023 in the Fall. This year was around the time I 
            took ownership of the team's development and overall direction that eventually followed my promotion to a lead.<br></br>
            <br></br>
            One massive change between Maxwell and Faraday is the switch from a tank, differential-style of drive to a swerve drive which
            allows the rover to strafe in any direction. This proved to be useful since we've previously burned a lot of competition time constantly adjusting the 
            rover's position and heading. Adopting this style of drive has saved us a lot of future headaches in that regard. This rover also sports a <u>NVIDIA Jetson AGX Orin</u>, allowing
            us to migrate our software to <u>ROS2 Humble</u> and gain access to better tools and libraries.<br></br> 
            <br></br>
            <figure>
              <img src={`${process.env.PUBLIC_URL}/media/mmrt/maxwell.jpg`} alt="Faraday with Arm (V1)"></img>
              <figcaption>Early Version of Maxwell on a Test Stand.</figcaption>
            </figure>
            The drivetrain is composed of four swerve modules, each of which has a BLDC drive motor and NEMA stepper for actuating the swivel. These motors are connected to their
            own respective motor controller boards (VESC and RAD as they are called) and speed/position commands are received from the Jetson via <u>CAN bus</u>. 
            Due to the added complexity, I put great care in ensuring the drive runs smoothly; <u>Apply ramping to the drive motors to prevent sudden jerks, created and tested a single
            ackermann drive mode alongside the standard swerve mode in case the swerves fail mid-task, implement wrap-around logic when the swerves pivot past their 240 degree limit, 
            add a calibration sequence before driving to ensure all wheels are aligned, and a ton of PID tuning</u>. Unfortunately, things like this are never perfect, the poor tolerances between the
            stepper motor input and output of the swerve modules caused enough mechanical backlash that the wheels would desync over time with the only remedy to recalibrate them very few minutes
            on the field. The desyncing is noticable even after 20 seconds of driving:
            <video controls>
              <source src={`${process.env.PUBLIC_URL}/media/mmrt/maxwell_rad.mp4`} type="video/mp4" />
            </video>
            <br></br>Despite this being a major issue, we were able to get by with periodic recalibrations and the ~10 degrees of slack on the swivel joints actually helped force the wheels
            back into alignment automatically when on the floor. I have since learned the importance of not only good mechanical design and iterative prototyping, but integration and coordination
            between different teams which, had we approached this from the perspective of building reliable control systems, might have prevented this problem all together.<br></br>
            <br></br>
            As for how I implemented the controls, the following diagram depicts the overall ROS architecture of the drivetrain. Simply put, the joy node reads the state of the joystick and publishes it to an xbox node that maps the inputs to a velocity command 
            and broadcasts the resulting messages over a local network created by two rocketm2 Wi-Fi modules. Once received by the rover, <u>the commands are subscribed to a series of nodes that transforms the velocity into raw CAN frames designated to all
            VESC and drivetrain RADs which in turn control the motors at their specified speeds</u>. This is how the rover is able to drive from a remote xbox controller on the basestation. 
            <figure className="rosgraph-figure">
              <img className="rosgraph-image" src={`${process.env.PUBLIC_URL}/media/mmrt/rosgraph.png`} alt="ROS Drivetrain Architecture"></img>
              <figcaption>ROS Graph of Maxwell's Drivetrain Architecture.</figcaption>
            </figure>
            <div style={{display: 'flex', 'align-items': 'center', gap: '20px'}}>
              <p>
              Apart from the drivetrain, I also <u>developed firmware for Maxwell's Onboard Controller board or OBC which is a custom PCB that interfaces with sensors and devices such as GNSS,
              IMU, LoRa, and PWM controllers using a Teensy 4.1 microcontroller</u>. The Teensy communicates directly with the Jetson over ethernet on a UDP4 network, and this communication
              is facilitated by the <u>microROS library</u>, enabling for seamless integration between the two. Incoming GNSS and IMU data are published directly to ROS topics which will later
              be fused in conjunction with other external sensors through an EKF for more accurate localization.
              </p>
              <figure className="dmc-figure">
                <img src={`${process.env.PUBLIC_URL}/media/mmrt/OBC.jpg`} alt="OBC Board"></img>
                <figcaption>OBC V1.1 with Teensy 4.1</figcaption>
              </figure>
            </div>
            Even though Maxwell is much more advanced than Faraday, it still comes up short in many areas that impacted our performances at CIRC 2024 and 2025. Many of the electrical
            components that were originally off the shelf were custom designed by the team, projects were more ambitious, and the increase in R&D introduced friction in testing and refining the rover. Still, <u>I gained a lot
            of invaluable experience with ROS, CAN, and designing software for robotic systems.</u>
            <div className="sources-subsection">
              <h3>Extra Sources</h3>
              <ul>
                <li><a href="https://github.com/MacRover/maxwell" target="_blank" rel="noreferrer">MacRover/maxwell</a></li>
              </ul>
            </div>
          </>
        )
      },
      { 
        title: 'Curie (2025 - Present)', 
        body: (
            <>
                Curie is the team's third rover spawned from the necessity to simplify the design and prioritize reliability using the lessons learned from
                both Faraday and Maxwell. This version is still in a work in progress and will continue to be worked on throughout the year.<br></br>
                <br></br>
                The arms used on previous rovers have never found much success up to this point, the main reasons being heavy assemblies and mechanisms, 
                complex linkages causing joints to lock, and backlash. Because of this, we've focused a ton of effort in building a new <u>5-DOF arm</u> that is 
                lighter, more compact, and trivial to control.<br></br>
                <br></br>
                <figure>
                    <img src={`${process.env.PUBLIC_URL}/media/mmrt/arm_v3.jpg`} alt="Curie Arm"></img>
                    <figcaption>V3 Arm On Table with Crude Harnessing, Ready for Testing.</figcaption>
                </figure>
                In place of the stepper motors we conventionally use, this arm is actuated using brushless NEO motors controlled by SparkMAX ESCs which are faster and
                far more powerful. SparkMAXes were designed primarily for use on FIRST Robotics Competition robots and therefore communicates over a CAN
                protocol specified by FIRST. For this to work, we <u>reverse-engineered a custom socketCAN driver to talk to these SparkMAX motor controllers 
                and provide a useful interface in our ROS stack to control the arm, drivetrain, and any other relevant subsystems</u>.
                <video controls>
                  <source src={`${process.env.PUBLIC_URL}/media/mmrt/sparkmax_drive_test.mp4`} type="video/mp4" />
                </video>
                <br></br>From there, I <u>implemented a hardware plugin in ROS2 that interacts directly with all SparkMAXes on the arm using the CAN library mentioned above
                and map the joints to a ROS2 joint trajectory controller for higher-level control</u>. This is paired with moveit2, a popular ROS kinematics framework, to
                generate and execute motion plans for the arm. The video below demonstrates the arm using moveit to move from one position to another and cycles back and forth between
                the two joint states:
                <iframe title="Curie Arm Demo" src="https://youtube.com/embed/F4kLkjfgRus" allow="fullscreen"></iframe>
                <div className="sources-subsection">
                  <h3>Extra Sources</h3>
                  <ul>
                    <li><a href="https://github.com/MacRover/spark_mmrt" target="_blank" rel="noreferrer">MacRover/spark_mmrt</a></li>
                    <li><a href="https://github.com/MacRover/curie" target="_blank" rel="noreferrer">MacRover/curie</a></li>
                  </ul>
                </div>
            </>
        )  
      },
    ]
  },
  {
    id: 'mc_turing_machine', 
    title: 'Minecraft Computer', 
    year: '2024', 
    front_image: '', 
    front_subtitle: '',
    gallery: [
      'media/tm/tm_tape.png',
    ],
    description: 'Turing machine built entirely in vanilla Minecraft using redstone.',
    catchphrase: 'Minecraft Turing Machine',
    details: (
      <>
      This is a Turing machine created within the confines of Minecraft's redstone and mechanics, capable of performing the same computations as its
      theroretical model. It is complete with a memory tape, read/write head, and program represented as a finite state machine.
      <iframe title="Minecraft Turing Machine" src="https://youtube.com/embed/GBBxEfKeFk4" allow="fullscreen"></iframe>
      <iframe className="pdf-embed" title="Minecraft Turing Machine Report" src={`${process.env.PUBLIC_URL}/media/tm/report.pdf`}></iframe>
      </>
    ),
    sections: []
  },
  {
    id: 'frc772',
    title: 'FRC Autonomous Systems',
    year: '2019-2022',
    front_image: '',
    front_subtitle: '',
    gallery: [
      'media/frc/limelight.jpg',
      'media/frc/chairmans.jpg',
      'media/frc/windsor2022pit.jpg',
    ],
    description: 'A bunch of visual and field-oriented localization and auto work I did for FRC Team 772.',
    catchphrase: 'My FIRST Time with Robots',
    details: (
      <>
      The <a href="https://www.firstinspires.org/robotics/frc" target="_blank" rel="noreferrer">FIRST Robotics Competition (FRC)</a> is an international high school 
      robotics competition in which teams of students design, build, and program robots to compete against each other in groups of three called alliances. 
      The games that facilitate the rules, tasks, and theme of the challenge change every year and are designed to be extremely fast-paced. To make our robots more competitive
      in such environments, <u>I took up the brunt of developing reliable autonomous routines and a field-oriented automated turret</u> for
      the games presented in 2020 and 2022, <a href="https://youtube.com/watch?v=gmiYWTmFRVE" target="_blank" rel="noreferrer">
      Infinite Recharge</a> and <a href="https://youtube.com/watch?v=LgniEjI9cCM" target="_blank" rel="noreferrer">Rapid React</a>.
      </>
    ),
    sections: [
      {
        title: '',
        body: (
          <>
          For context, every main game loop generally falls under two catagories: stacking and shooting, and both Infinite Recharge and Rapid
          React happen to be shooter-based games, meaning the primary way to earn points is to shoot game pieces into a goal. Their metagames
          are quite similar, so both of our robots are fashioned with similar mechanisms; An intake roller to collect the balls, a conveyor to lift them
          up to the turret, and flywheel and hood to shoot and adjust the trajectory of the flinging balls respectively. Because they're built nearly identical, I'll be describing the work 
          related to both of them within the same section.<br></br>
          <div style={{display: 'flex', 'align-items': 'center', gap: '20px'}}>
            <iframe style={{width: '50%' }} title="FRC Team 772 Robot Reveal 2020" src="https://www.youtube.com/embed/-VKoqZA7Nwk" allow="fullscreen"></iframe>
            <figure>
              <img src={`${process.env.PUBLIC_URL}/media/frc/post2022.jpg`} alt="FRC Team 772 Robot 2022"></img>
              <figcaption>The 2022 Comp Bot!</figcaption>
            </figure>
          </div>
          Mounted on the turrets is a Limelight camera to track key game elements using green LEDs to illuminate the field. It can be configured for fiducials 
          like apriltags and even perform basic object detection, but for us, its primary use is to detect the retro-reflective tape placed around the goal.<br></br>
          <br></br>
          Tuning the CV thresholds to filter the green hue reflected off the target combined with a single P-controller based on horizontal error from the cameras optical axis
          yielded promising results:
          <video controls>
            <source src={`${process.env.PUBLIC_URL}/media/frc/target_test.mp4`} type="video/mp4" />
          </video>
          <br></br>Here is also an older video of me testing out the Limelight for the first time, its fixed directly onto the chassis and uses the drivetrain to align
          the robot with the target as I move around:
          <video controls>
            <source src={`${process.env.PUBLIC_URL}/media/frc/chassis_limelight.mp4`} type="video/mp4" />
          </video>
          <br></br> Although it is very proficient at vision tracking, its major flaw of course is that it can only work when the target is in view, which may not always be the case 
          since both the vertical and horizontal FOVs are quite narrow (around 60 degrees for a Limelight 3). It also has no knowledge of the robot's position on the field, so the only way to relatch
          onto a target is manually drive either the turret or the drivetrain until it is back in view which is not ideal.<br></br>
          <br></br>
          To address this, <u>I ended up implementing a crude field-oriented automated turret that uses odometry from the drivetrain and a gyro to localize the robot in 2D space, 
          and based on the x and y distances from its starting position, points the shooter in the general direction of the central hub</u>. That way, the goal will always be 
          in constant sight and all the Limelight has to do is fine-tune the aiming.<br></br> 
          <br></br>
          Let's say from a birds eye view, the robot is positioned on the upper-left side of the field as depicted in the diagram below and the turret currently isn't properly aligned with the hub.
          To determine θ<sub>error</sub> between the turret and the hub, we compute the arctan of the robot's cartesian distance relative to the hub to find θ<sub>center</sub> .
          Assuming the turret zero position is facing forward, we can formulate a general equation as θ<sub>error</sub> = π/2 - θ<sub>turret</sub> + (π - θ<sub>center</sub>)
          for π/2 &lt; θ<sub>center</sub> &lt; π where θ<sub>turret</sub> is the angle between the turret and zero position. I then simply repeated the derivation for the rest of the quadrants.<br></br>
          <br></br>
          <img style={{'max-width': '750px', 'aspect-ratio': '1' }} src={`${process.env.PUBLIC_URL}/media/frc/autoturret_principle.png`} alt="Turret Diagram"></img>
          <br></br>
          This error is fed into a P-controller to dynamically adjust the turret's position as needed. As for the shooter, <u>I created a python script that produces a polynomial regression model 
          based on multiple samples of flywheel RPMs and hub distances (link <a href="https://github.com/chosterto/Regression-Analyzer" target="_blank" rel="noreferrer">here</a>)</u>. This outputted 
          a linear relationship that we can use to "reliably" score from virtually any position on the field:<br></br>
          <br></br>
          <figure>
            <img src={`${process.env.PUBLIC_URL}/media/frc/flywheel_graph.png`} alt="Flywheel Graph"></img>
            <figcaption>Flywheel RPM vs. Central Hub to Robot Distance.</figcaption>
          </figure>
          There also exists a 15 second autonomous period at the beginning of each match where the robot must operate without any human input and score points. In the 2022 game,
          the balls are scattered across the field in pre-defined locations and the robot must traverse and collect each of them to shoot into the hub. One method would be
          to simply record the distances between each ball and hardcode them into the routine, and in fact that is how we've always done it in the past, 2020 included,
          which is why our robots always moved in straight lines during auto modes. However, there is a limitation to this approach, and we noticed very quickly that 
          if the robot is knocked off its course for whatever reason, it will not recover and the whole routine will fail.<br></br>
          <br></br>
          We clearly needed something more robust and can adapt to its environment. In tandem with the field-oriented turret, <u>I also developed a pure pursuit controller
          with built-in waypoint interpolation to generate smooth paths for the robot to follow</u>. Although it may appear complicated, the principle is actually quite simple
          and the entire algorithm is documented on a report written by <a href={`${process.env.PUBLIC_URL}/media/frc/adaptive_pp_controller_1712.pdf`} target="_blank" rel="noopener noreferrer">Team 1712</a> that 
          I used extensively as a reference when writing my own <a href={`https://github.com/chosterto/PurePursuitTracking`} target="_blank" rel="noopener noreferrer">implementation</a>.<br></br>
          <br></br>
          The autonomous routine for scoring five balls into the goal is as follows: First, the robot drives off the alliance area with a pre-loaded ball, collects another ball and 
          shoots both of them into the hub. Secondly, it rotates in place and advances to the second ball and shoots it. Thirdly, it uses the adaptive 
          pure pursuit path follower to manuver toward the loading terminal to collect two more balls. Finally, the robot closes the distance to the hub and launches the remaining balls.<br></br>
          <br></br>
          <img style={{'max-width': '750px', 'aspect-ratio': '7/3' }} src={`${process.env.PUBLIC_URL}/media/frc/field.png`} alt="Autonomous Path of Robot"></img>
          <br></br>Some video demos on a practice field for those that are interested:
          <video controls>
            <source src={`${process.env.PUBLIC_URL}/media/frc/fiveball_auto_demo1.mp4`} type="video/mp4" />
          </video>
          <video controls>
            <source src={`${process.env.PUBLIC_URL}/media/frc/fiveball_auto_demo2.mp4`} type="video/mp4" />
          </video>
          <h2>Reflection</h2>
          Looking back, I am extremely proud to have been a part of this team and all the work I poured into it. Admittedly, we never ended up performing as well as we should have, and
          the demos shown, although impressive, were not indicative of actual play in competition. The five ball auto didn't work on its first few runs since the
          hand-measured practice field we used for testing was super inaccurate, and it wasn't until our final district event (St. Mary) that we got it working
          consistently. The balls also deflated over time and impacted our shooting, the Limelight would occasionally confuse targets with other reflective surfaces and bright lights,
          and so many other factors that I can barely talk about without going further into detail. Nonetheless, the technical and team-building experiences I gained were more than worth the
          effort, and I almost wish I could go back for one more year because of COVID taking a whole season away from me. I likely wouldn't have discovered my passion for
          engineering without FIRST, and I think many others share the same sentiment.<br></br>
          <br></br>
          <img style={{'max-width': '500px'}} src={`${process.env.PUBLIC_URL}/media/frc/windsor2019.jpg`} alt="FRC Team 772"></img>
          </>
        )
      }
    ]
  },
  {
    id: 'legacy',
    title: 'Legacy Stuff',
    year: '',
    front_image: '',
    front_subtitle: '',
    gallery: [
      'media/legacy/wallet1.png',
      'media/legacy/wallet_parts.jpg',
      'media/legacy/wallet2.png',
    ],
    description: 'Old, miscellaneous, unfinished, and/or irrelevant projects are archived here.',
    catchphrase: '',
    details: (
      <>
        <i>Disclaimer:</i> The following is a collection of past personal projects that didn't make the cut, either because I didn't find them interesting enough 
        to talk about in depth, or they weren't appropriate to include in the main portfolio. I have them stored here purely for documentation purposes.
      </>
    ),
    sections: [
      {
        title: 'Cam Actuated Wallet (2026)',
        body: (
          <>
          <div style={{display: 'flex', 'align-items': 'center'}}>
            <img style={{'aspect-ratio': 'auto'}} src={`${process.env.PUBLIC_URL}/media/legacy/wallet1.png`} alt="Cam Actuated Wallet"></img>
            <img style={{'aspect-ratio': 'auto'}} src={`${process.env.PUBLIC_URL}/media/legacy/wallet2.png`} alt="Cam Actuated Wallet"></img>
          </div>
          <br></br>
          <div style={{display: 'flex', 'align-items': 'center', 'gap': '50px'}}>
            <img src={`${process.env.PUBLIC_URL}/media/legacy/wallet_parts.jpg`} alt="Cam Actuated Wallet"></img>
            A 3D printed wallet custom designed to eject cards outside of its housing. A turning knob is linked to a geneva mechanism that drives two separate gear trains connected
            to a respective cam shaft. Each shaft has two accentric cams oriented 180 degrees apart that pushes each cartridge out one by one. The wallet is designed to hold up to 8 cards
            and was modelled in Onshape. Overall a ton of trial and error was involved to balance out the tolerances and ensure the mechanism operates reliably. Both a nice
            fidget toy and a practical solution over more traditional bulkier designs, and I end up using this quite a lot in my day to day life. 
          </div>
          <iframe style={{"width": "auto", "height": "100%"}} src="https://www.youtube.com/embed/3bVucNLHtXM" title="Cam Actuated Wallet"></iframe>
          </>
        )
      },
      {
        title: 'Mechanical Clock (2025)',
        body: (
          <>
          The whole idea behind this originated from Flower 3D's wonderful 3D printed, manually push actioned 7-segment display; highly recommend checking 
          this video out for more details:
          <iframe title="7 Segment Counter v3.0, Tutorial, 3D Printed, STL Files Link" src="https://www.youtube.com/embed/LMwjKzrQjHg" allow="fullscreen"></iframe>
          <br></br>
          To tweak the existing design and create a fully functional mechanical clock, I replaced the push lever with a gear rack and pinion
          driven by micro servos controlled via a STM32 bluepill using PWM. Originally, the time was kept using the microcontroller's internal clock which worked to some
          degree, however over extended periods of time would drift away from the true time. An ESP-01S beacon was later connected to the bluepill to retrieve NTP time every half 
          past hour and reseed the time with up to one second of accuracy. Overall a very fun project to work on and I learned a lot about mechanical design and 3D printing.
          </>
        )
      },
      {
        title: 'FractGraph Plots (2023)',
        body: (
          <>
          A mini weekend project meant to visualize fractional differintegral plots of functions using plotly and python. The idea is derived from the Cauchy formula
          to compress nested integrals into a single integral, and replacing the discrete factorial within that formula with the gamma function for more
          continuous representation between the base function and its derivative/integral. Below is an example of such a plot with f(x) = x and its
          respective differintegral plots.<br></br>
          <br></br>
          <img style={{'max-width': '750px', 'aspect-ratio': '7/3'}} src={`${process.env.PUBLIC_URL}/media/legacy/fractgraphs.gif`} alt="FractGraph"></img>
          </>
        )
      },
      {
        title: 'Discord Game Bot (2022)',
        body: (
          <>
          <div style={{display: 'flex', 'align-items': 'center', 'gap': '50px'}}>
            <p>
              This was a quirky discord server bot I made mostly for fun and it comes packed with a few simple minigames like rock-paper-scissors, tic-tac-toe, mazes, and find the ball!
              Made using discord's python API, link to the repository <a href="https://github.com/chosterto/GameBot" target="_blank" rel="noreferrer">here</a>.
              Some additional features include maze cell generation using recursive backtracking, an optimal tic-tac-toe bot implemented with minimax algorithm, and
              interactive UI using embeds and buttons. 
            </p>
            <img style={{"aspect-ratio": "auto"}} src={`${process.env.PUBLIC_URL}/media/legacy/discord_maze.png`} alt="Discord Game Bot"></img>
          </div>
          </>
        )
      }
    ]
  }
];

function ProjectGallery({ project }) {
  const [activeImage, setActiveImage] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveImage((currentImage) => (currentImage + 1) % project.gallery.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [project.gallery.length]);

  return (
    <div className="project-collage project-fade-gallery" aria-label={`${project.title} project image collage`}>
      {project.gallery.map((image, index) => (
        <img
          key={image}
          className={index === activeImage ? 'is-active' : ''}
          src={`${process.env.PUBLIC_URL}/${image}`}
          alt={index === activeImage ? `${project.title} project` : ''}
          aria-hidden={index === activeImage ? undefined : true}
        />
      ))}
    </div>
  );
}

function ProjectCollage({ project }) {
  return (
    <div className="project-collage" aria-label={`${project.title} project image collage`}>
      {[...project.gallery, ...project.gallery].map((image, index) => (
        <img
          key={`${image}-${index}`}
          src={`${process.env.PUBLIC_URL}/${image}`}
          alt={index === 0 ? `${project.title} project` : ''}
          aria-hidden={index === 0 ? undefined : true}
        />
      ))}
    </div>
  );
}

function Projects() {
  return (
    <main className="page-shell projects-page">
      <div className="project-grid">
        {projects.map((project) => (
          <Link
            key={project.id}
            href={`/projects/${project.id}`}
            className="project-card"
          >
            <div className="project-image">
              {project.gallery.length > 1 ? (
                project.gallery.length < 10 ? <ProjectGallery project={project} /> : <ProjectCollage project={project} />
                ) : <img src={`${process.env.PUBLIC_URL}/${project.gallery[0]}`} alt="Oops, something went wrong" />
              }
              <span className="project-id">{project.id}</span>
            </div>
            <div className="project-card-copy">
              <div>
                <strong>{project.title}</strong>
                  {project.year}
              </div>
              <p>{project.description}</p>
            </div>
          </Link>
        ))}
      </div>
    </main>
  );
}

function ProjectDetail({ projectId }) {
  const project = projects.find((item) => item.id === projectId);
  const next = projects[(projects.indexOf(project) + 1) % projects.length];
  const [openSections, setOpenSections] = useState([]);

  function toggleSection(index) {
    setOpenSections((currentSections) => currentSections.includes(index)
      ? currentSections.filter((sectionIndex) => sectionIndex !== index)
      : [...currentSections, index]);
  }

  return (
    <article className="page-shell detail-page">
      <div className="detail-topline">
        <Link href="/projects" className="back-link">
          Back to projects
        </Link>
        <Link href={`/projects/${next.id}`} className="back-link">
          Next Project
        </Link>
      </div>
      <header className="detail-heading">
        <h1>{project.catchphrase}</h1>
        { project.front_image ? 
        <figure>
            <img src={`${process.env.PUBLIC_URL}/${project.front_image}`} alt={`${project.title} project`} />
            <figcaption>{project.front_subtitle}</figcaption>
        </figure>
         : null}
      </header>
      <section className="article-overview">
        <span className="article-label">Overview</span>
        <p>{project.details}</p>
      </section>
      <section className="article-sections" aria-label="Project sections">
        {(project.sections.length > 1 || !project.sections.length) ? project.sections.map((section, index) => (
          <div className={`article-section ${openSections.includes(index) ? 'is-open' : ''}`} key={section.title}>
            <button
              className="article-section-toggle"
              type="button"
              aria-expanded={openSections.includes(index)}
              aria-controls={`project-section-${index}`}
              onClick={() => toggleSection(index)}
            >
              <span>{String(index + 1).padStart(2, '0')}</span>
              {section.title}
              <b aria-hidden="true">+</b>
            </button>
            <div className="article-section-body">
              <div className="article-section-content">{section.body}</div>
            </div>
          </div>
        )) : (
          <div className="article-section">
            <div className="article-section-body-single">
              <div className="article-section-content">{project.sections[0].body}</div>
            </div>
          </div>
        )}
      </section>
    </article>
  );
}

export { Projects, ProjectDetail };